import React, { useCallback, useEffect, useRef, useState } from 'react';

import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import { Box, IconButton, Radio, Typography } from '@mui/material';

import ImageCarouselSkeleton from './Skeleton';
import * as S from './styles';
import Button from 'components/Button';
import { useThemeContext } from 'global/ThemeContext/ThemeContext';
import { useStrapiBannersCockpit } from 'hooks/useStrapiBannersCockpit';

const ImageCarousel: React.FC = () => {
  const theme = useThemeContext();
  const { banners, isLoading, error } = useStrapiBannersCockpit();
  const carouselRef = useRef<HTMLDivElement>(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [isPaused, setIsPaused] = useState(false);

  const cardsPerPage = 3;
  const totalPages = Math.ceil((banners?.cards?.length || 0) / cardsPerPage);

  const moveToPage = (page: number) => {
    if (carouselRef.current) {
      const cardWidth = (carouselRef.current.firstChild as HTMLElement)?.clientWidth || 0;
      const gap = 10;
      const scrollLeft = page * (cardsPerPage * (cardWidth + gap));
      carouselRef.current.scrollTo({
        left: scrollLeft,
        behavior: 'smooth',
      });
      setCurrentPage(page);
    }
  };

  const moveToNext = useCallback(() => {
    const nextPage = (currentPage + 1) % totalPages;
    moveToPage(nextPage);
  }, [currentPage, totalPages]);

  const moveToPrev = useCallback(() => {
    const prevPage = (currentPage - 1 + totalPages) % totalPages;
    moveToPage(prevPage);
  }, [currentPage, totalPages]);

  useEffect(() => {
    if (!isPaused) {
      const interval = setInterval(() => {
        moveToNext();
      }, 5000);
      return () => clearInterval(interval);
    }
  }, [currentPage, isPaused, moveToNext]);

  if (isLoading) {
    return <ImageCarouselSkeleton />;
  }

  if (error) {
    return (
      <Box
        sx={{
          textAlign: 'center',
          padding: '2rem',
          color: theme.text.secondary,
        }}
      >
        <Typography variant="body2" color="textSecondary">
          Carregando banners...
        </Typography>
        <Typography
          variant="caption"
          color="textSecondary"
          sx={{ display: 'block', marginTop: '0.5rem' }}
        >
          Aguarde um momento enquanto os dados são carregados automaticamente.
        </Typography>
      </Box>
    );
  }

  if (!banners?.cards?.length) {
    return (
      <Box
        sx={{
          textAlign: 'center',
          padding: '2rem',
          color: theme.text.secondary,
        }}
      >
        <Typography variant="body2" color="textSecondary">
          Nenhum banner disponível no momento.
        </Typography>
        <Typography
          variant="caption"
          color="textSecondary"
          sx={{ display: 'block', marginTop: '0.5rem' }}
        >
          Os banners serão carregados automaticamente quando estiverem disponíveis.
        </Typography>
      </Box>
    );
  }

  return (
    <S.CarouselWrapper
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
    >
      <S.CarouselContainer ref={carouselRef}>
        {banners.cards.map((card, index) => (
          <S.ImageCard
            key={card.id}
            active={currentPage === index}
            width={card.media.width}
            height={card.media.height}
          >
            <img src={card.media.url} alt={card.alt} draggable={false} />
            {card.link && (
              <S.StyledButton>
                <Button
                  onClick={() => window.open(card.link, '_blank')}
                  style={{
                    color: theme.text.secondary,
                    borderRadius: '0.5rem',
                    height: '24px',
                    opacity: 0.5,
                  }}
                  onMouseEnter={(e: any) => (e.currentTarget.style.opacity = '1')}
                  onMouseLeave={(e: any) => (e.currentTarget.style.opacity = '0.3')}
                >
                  {card.cta}
                </Button>
              </S.StyledButton>
            )}
          </S.ImageCard>
        ))}
      </S.CarouselContainer>

      <S.IndicatorWrapper>
        <IconButton
          sx={{
            color: theme.primary,
          }}
          onClick={moveToPrev}
        >
          <KeyboardArrowLeftIcon />
        </IconButton>

        {Array.from({ length: totalPages }, (_, index) => (
          <Radio
            key={index}
            checked={currentPage === index}
            onChange={() => moveToPage(index)}
            size="small"
            sx={{
              //width: '0.25rem',
              //height: '0.25rem',
              color: theme.text.primary,
              '&.Mui-checked': {
                color: theme.primary,
              },
            }}
          />
        ))}

        <IconButton
          sx={{
            color: theme.primary,
          }}
          onClick={moveToNext}
        >
          <KeyboardArrowRightIcon />
        </IconButton>
      </S.IndicatorWrapper>
    </S.CarouselWrapper>
  );
};

export default ImageCarousel;
